import {
  APP_INITIALIZER,
  EnvironmentProviders,
  ErrorHandler,
  InjectionToken,
  Provider,
} from "@angular/core";
import { DecimalPipe } from "@angular/common";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { MessageService } from "primeng/api";
import { catchError, iif, mergeMap, of } from "rxjs";

import { TranslateService } from "@app/shared/_services/translate.service";
import { UserService } from "@app/shared/_services/user.service";
import { AuthenticationService } from "@app/shared/_services/authentication.service";
import { MockService } from "@app/shared/_services/mock.service";
import { DomainConfigService } from "@app/shared/_services/domain.config.service";
import { JwtInterceptor } from "@app/shared/_helpers/jwt.interceptor";
import { GlobalErrorHandler } from "@app/shared/_helpers/globalerror.handler";
import { IsLoggedIn } from "@app/shared/_helpers/islogged";
import { setupUserConfig } from "@app/shared/_factories/config.factory";
import { User } from "@app/shared/_interfaces/user";
import { CKEditor4 } from "ckeditor4-angular";

export const WYSIWYG_CONFIG = new InjectionToken<CKEditor4.Config>(
  "wysiwyg_config",
);

export const appProviders: (Provider | EnvironmentProviders)[] = [
  IsLoggedIn,
  MessageService,
  CookieService,
  DecimalPipe,
  { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
  {
    provide: APP_INITIALIZER,
    useFactory: setupUserConfig,
    deps: [TranslateService, UserService, AuthenticationService, MockService],
    multi: true,
  },
  {
    provide: WYSIWYG_CONFIG,
    useFactory: (translateService: TranslateService) => ({
      linkShowTargetTab: true,
      language: translateService.getCurrentLanguage(),
      extraPlugins: "codemirror",
      line_height:
        "auto;6px;8px;10px;12px;14px;18px;22px;24px;26px;28px;30px;36px;48px;60px;72px",
      lineHeight_style: {
        element: "p",
        styles: { "line-height": "#(size)" },
        overrides: [
          {
            element: "line-height",
            attributes: { size: null },
          },
        ],
      },
      toolbar: [
        {
          name: "textstyle",
          items: ["Font", "Bold", "Italic", "Underline", "Strike"],
        },
        { name: "textstylesize", items: ["FontSize", "lineheight"] },
        { name: "links", items: ["Link", "Unlink", "msc_toolbar_1"] },
        { name: "colors", items: ["TextColor", "BGColor"] },
        {
          name: "alignment",
          items: [
            "JustifyLeft",
            "JustifyRight",
            "JustifyCenter",
            "JustifyBlock",
          ],
        },
        { name: "lists", items: ["NumberedList", "BulletedList"] },
        { name: "subsuperscript", items: ["Subscript", "Superscript"] },
        { name: "extrachars", items: ["Emojione", "Symbol"] },
        { name: "remove", items: ["RemoveFormat"] },
        { name: "images", items: ["Image"] },
        { name: "source", items: ["Source"] },
      ],
      position: "center",
      magicline: true,
      magicline_color: "#000000",
      startupFocus: true,
      forcePasteAsPlainText: true,
    }),
    deps: [TranslateService],
  },
  {
    provide: APP_INITIALIZER,
    useFactory: (domainConfigService: DomainConfigService) => {
      return () => {
        return new Promise((resolve) => {
          const defaultResponse$ = of(<User.SubaccountConfig>{
            name: "turboSMTP",
            logo: "/assets/img/logo_full.svg",
            custom: false,
          });
          of(document.location.hostname)
            .pipe(
              mergeMap((hostName) =>
                iif(
                  () =>
                    !hostName.includes("serversmtp.com") &&
                    !hostName.includes("localhost"),
                  domainConfigService.getConfig(hostName),
                  defaultResponse$,
                ),
              ),
              catchError(() => defaultResponse$),
            )
            .subscribe({
              next: (config) => {
                domainConfigService.setUserSubaccountConfig(config);
                resolve(config);
              },
            });
        });
      };
    },
    deps: [DomainConfigService],
    multi: true,
  },
  {
    provide: ErrorHandler,
    useClass: GlobalErrorHandler,
    deps: [TranslateService],
  },
];
